import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, ArrowUpRight } from 'lucide-react';

const ContactSection = () => {
  const teamContacts = [
    { role: 'Managing Director', phone: '+971 52 9292511', email: null },
    { role: 'Project Manager', phone: '+971 52 5163943', email: 'jerome@rmefandbeqpt.com' },
    { role: 'Senior Designer', phone: '+971 52 6058807', email: 'kelvin@rmefandbeqpt.com' },
    { role: 'Designer', phone: null, email: 'vincent@rmefandbeqpt.com' },
    { role: 'Estimation', phone: '+971 56 4089392', email: 'maricris@rmefandbeqpt.com' },
    { role: 'Doc Controller', phone: null, email: 'ashraf@rmefandbeqpt.com' },
    { role: 'Business Development Executive', name: 'Suhaina', phone: null, email: 'suhaina@rmeinternational.onmicrosoft.com' },
  ];

  return (
    <section
      id="contact"
      className="py-24 md:py-32 lg:py-40"
      data-testid="contact-section"
    >
      {/* Top Section - Dark */}
      <div className="bg-[#2D3436] py-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <p className="text-xs uppercase tracking-[0.3em] font-medium text-[#D4C4A8] mb-4" data-testid="contact-overline">
              Get in Touch
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl font-light tracking-tight text-white mb-6" data-testid="contact-heading">
              Let's Create Together
            </h2>
            <div className="w-16 h-px bg-[#D4C4A8] mx-auto"></div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 md:px-12 lg:px-24 -mt-1">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            
            {/* Left - Map */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
              data-testid="contact-map"
            >
              <div className="aspect-square lg:aspect-auto lg:h-full min-h-[400px] bg-[#E8E2D9]">
                <iframe
                  src="https://maps.google.com/maps?q=Dubai+Investment+Park+Phase+2&t=&z=14&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(100%) contrast(1.1) brightness(1.05)' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                  className="hover:grayscale-0 transition-all duration-1000"
                ></iframe>
              </div>
              
              {/* Address Overlay */}
              <a
                href="https://www.google.com/maps/search/?api=1&query=Dubai+Investment+Park+Phase+2+Dubai+UAE"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-0 left-0 right-0 bg-white p-6 flex items-center justify-between hover:bg-[#F5F1EB] transition-colors duration-300 border-t border-[#E5E0D5]"
                data-testid="google-maps-link"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 border border-[#2C6E63] flex items-center justify-center">
                    <MapPin size={18} className="text-[#2C6E63]" />
                  </div>
                  <div>
                    <p className="font-heading text-[#2D3436]">Dubai Investment Park (DIP) – Phase 2</p>
                    <p className="text-sm text-[#6B6560] font-light">P.O. Box 24381, Dubai, UAE</p>
                  </div>
                </div>
                <ArrowUpRight size={18} className="text-[#2C6E63]" />
              </a>
            </motion.div>

            {/* Right - Contact Details */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-[#2D3436] p-8 lg:p-12"
            >
              {/* Main Contact */}
              <div className="mb-10">
                <h3 className="text-xs uppercase tracking-[0.2em] text-[#D4C4A8] mb-6">Contact</h3>
                <div className="space-y-4">
                  <a
                    href="tel:+97148898577"
                    className="flex items-center gap-4 text-white hover:text-[#D4C4A8] transition-colors group"
                    data-testid="contact-landline"
                  >
                    <div className="w-10 h-10 border border-white/20 flex items-center justify-center group-hover:border-[#D4C4A8] transition-colors">
                      <Phone size={16} />
                    </div>
                    <span className="font-heading text-lg">+971 4 8898577</span>
                  </a>
                  <a
                    href="mailto:arun@rmefandbeqpt.com"
                    className="flex items-center gap-4 text-white hover:text-[#D4C4A8] transition-colors group"
                    data-testid="contact-main-email"
                  >
                    <div className="w-10 h-10 border border-white/20 flex items-center justify-center group-hover:border-[#D4C4A8] transition-colors">
                      <Mail size={16} />
                    </div>
                    <span className="font-heading text-lg">arun@rmefandbeqpt.com</span>
                  </a>
                </div>
              </div>

              {/* Team */}
              <div className="mb-10">
                <h3 className="text-xs uppercase tracking-[0.2em] text-[#D4C4A8] mb-6">Our Team</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {teamContacts.map((contact, index) => (
                    <div
                      key={index}
                      className="p-4 border border-white/10 hover:border-white/30 transition-colors"
                      data-testid={`team-contact-${index}`}
                    >
                      <p className="text-white font-heading text-sm mb-2">
                        {contact.role}
                        {contact.name && <span className="text-[#D4C4A8]"> — {contact.name}</span>}
                      </p>
                      <div className="space-y-1">
                        {contact.phone && (
                          <a href={`tel:${contact.phone.replace(/\s/g, '')}`} className="block text-white/50 text-xs hover:text-[#D4C4A8] transition-colors">
                            {contact.phone}
                          </a>
                        )}
                        {contact.email && (
                          <a href={`mailto:${contact.email}`} className="block text-white/50 text-xs hover:text-[#D4C4A8] transition-colors truncate">
                            {contact.email}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <a
                href="mailto:arun@rmefandbeqpt.com"
                className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#D4C4A8] text-[#2D3436] font-heading tracking-wide hover:bg-[#C9B896] transition-all duration-300"
                data-testid="contact-cta-button"
              >
                Start Your Project
                <ArrowUpRight size={16} />
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
